import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

export const EASE = [0.16, 1, 0.3, 1];

export function Logo({ footer = false }) {
  const { t } = useLanguage();
  const w = t.wordmark;
  const main = (
    <span className={`font-display font-extrabold uppercase tracking-[0.22em] text-white ${footer ? 'text-xl' : 'text-lg'}`}>
      {w.main}
    </span>
  );
  const sub = (
    <span className={`font-display font-medium uppercase tracking-[0.3em] text-[#C6A15B] ${footer ? 'text-sm' : 'text-xs'}`}>
      {w.sub}
    </span>
  );
  return (
    <Link
      to="/"
      aria-label={`DELUXE — home`}
      data-testid={footer ? 'footer-logo' : 'header-logo'}
      className="flex items-baseline gap-2.5"
    >
      {w.reversed ? (
        <>
          {sub}
          {main}
        </>
      ) : (
        <>
          {main}
          {sub}
        </>
      )}
    </Link>
  );
}

export function Eyebrow({ children, dark = false, className = '' }) {
  return (
    <Reveal y={12} className={className}>
      <p
        className={`flex items-center gap-3 font-mono text-[11px] font-semibold uppercase tracking-[0.3em] sm:text-xs ${
          dark ? 'text-[#C6A15B]' : 'text-forest'
        }`}
      >
        <span className={`h-px w-8 ${dark ? 'bg-[#C6A15B]' : 'bg-forest'}`} aria-hidden="true" />
        {children}
      </p>
    </Reveal>
  );
}

export function MaskedLines({ lines, as = 'div', className = '', delay = 0, inView = false }) {
  const Tag = as;
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    if (!inView) {
      const id = requestAnimationFrame(() => setSeen(true));
      return () => cancelAnimationFrame(id);
    }
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setSeen(true);
          io.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [inView]);

  return (
    <Tag ref={ref} className={className}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden pb-[0.09em] -mb-[0.09em]">
          <span
            className="block transition-transform duration-[800ms] ease-[cubic-bezier(0.16,1,0.3,1)]"
            style={{
              transform: seen ? 'translateY(0%)' : 'translateY(115%)',
              transitionDelay: `${Math.round((delay + i * 0.09) * 1000)}ms`,
            }}
          >
            {line}
          </span>
        </span>
      ))}
    </Tag>
  );
}

export function Reveal({ children, delay = 0, y = 30, className = '' }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, ease: EASE, delay }}
    >
      {children}
    </motion.div>
  );
}

const buttonVariants = {
  green: 'bg-forest text-white hover:bg-forest-dark',
  gold: 'bg-[#C6A15B] text-ink hover:bg-[#b8924c]',
  white: 'bg-white text-ink hover:bg-cream',
  outlineWhite: 'border border-white/40 text-white hover:bg-white hover:text-ink',
  outlineDark: 'border border-ink/25 text-ink hover:bg-ink hover:text-white',
};

export function ArrowButton({ to, onClick, type, variant = 'green', children, dataTestId, className = '', disabled = false }) {
  const cls = `group inline-flex items-center justify-center gap-3 px-7 py-4 font-display text-xs font-bold uppercase tracking-[0.18em] transition-colors duration-300 disabled:cursor-not-allowed disabled:opacity-60 sm:text-sm ${buttonVariants[variant]} ${className}`;
  const arrow = <ArrowRight className="h-4 w-4 shrink-0 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />;
  if (to) {
    return (
      <Link to={to} className={cls} data-testid={dataTestId}>
        {children}
        {arrow}
      </Link>
    );
  }
  return (
    <button type={type || 'button'} onClick={onClick} disabled={disabled} className={cls} data-testid={dataTestId}>
      {children}
      {arrow}
    </button>
  );
}

export function Marquee({ items }) {
  return (
    <div className="overflow-hidden border-y border-forest/10 bg-cream-muted py-5" aria-hidden="true">
      <div className="flex w-max animate-marquee">
        {[0, 1].map((dup) => (
          <div key={dup} className="flex items-center">
            {items.map((item, i) => (
              <span key={i} className="flex items-center">
                <span className="whitespace-nowrap px-8 font-display text-sm font-bold uppercase tracking-[0.25em] text-forest/70">
                  {item}
                </span>
                <span className="inline-block h-1.5 w-1.5 rotate-45 bg-[#C6A15B]" />
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function SectionHead({ eyebrow, lines, sub, dark = false, className = '' }) {
  return (
    <div className={className}>
      <Eyebrow dark={dark}>{eyebrow}</Eyebrow>
      <MaskedLines
        as="h2"
        lines={lines}
        inView
        delay={0.1}
        className={`mt-6 font-display text-3xl font-extrabold uppercase leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl ${
          dark ? 'text-white' : 'text-ink'
        }`}
      />
      {sub && (
        <Reveal delay={0.2}>
          <p className={`mt-6 max-w-xl text-base leading-relaxed sm:text-lg ${dark ? 'text-white/70' : 'text-neutral-600'}`}>
            {sub}
          </p>
        </Reveal>
      )}
    </div>
  );
}

export function TikTokIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.9 2.9 0 1 1-2.31-2.84v-3.5a6.37 6.37 0 1 0 5.76 6.33V7.69a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.12Z" />
    </svg>
  );
}
